import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Form,
  InputGroup,
  Spinner,
  Table,
} from "react-bootstrap";
import { Save, Search } from "react-feather";
import { useGet, usePut } from "../../api";
import { IUser, IUsersResponse, IUserWithGroups } from "../../interfaces";

const PAGE_SIZE = 25;

export const Users: React.FC = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const query = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  if (search.trim()) query.set("search", search.trim());

  const { data, loading, error, refetch } = useGet<IUsersResponse>(
    `users?${query.toString()}`
  );
  const { execute: updateUser } = usePut<IUser, { name: string }>();
  const [names, setNames] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const currentName = (user: IUser) => names[user._id] ?? user.name ?? "";

  const saveName = async (user: IUser) => {
    setSavingId(user._id);
    setSaveError(null);
    try {
      await updateUser(`users/${user._id}`, { name: currentName(user) });
      setNames((current) => {
        const next = { ...current };
        delete next[user._id];
        return next;
      });
      await refetch();
    } catch (requestError: any) {
      setSaveError(
        requestError?.data?.error ||
          requestError?.message ||
          "Failed to update user"
      );
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Container className="py-3">
      <div className="d-flex justify-content-between align-items-end gap-3 mb-3">
        <div>
          <h2 className="mb-1">Users</h2>
          <p className="text-muted mb-0">
            Set the names shown across QA and work pipeline activity.
          </p>
        </div>
        <InputGroup style={{ maxWidth: 320 }}>
          <InputGroup.Text>
            <Search size={16} />
          </InputGroup.Text>
          <Form.Control
            aria-label="Search users"
            placeholder="Search name or email"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </InputGroup>
      </div>

      {(error || saveError) && (
        <Alert variant="danger">
          {saveError || error?.data?.error || error?.message}
        </Alert>
      )}

      {loading && !data ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <>
          <Table bordered hover responsive className="align-middle mb-2">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>User groups</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {(data?.results ?? []).map((user: IUserWithGroups) => {
                const name = currentName(user);
                const changed = name.trim() !== (user.name ?? "");
                return (
                  <tr key={user._id}>
                    <td style={{ minWidth: 240 }}>
                      <Form.Control
                        value={name}
                        maxLength={100}
                        placeholder={user.email}
                        onChange={(event) =>
                          setNames((current) => ({
                            ...current,
                            [user._id]: event.target.value,
                          }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && changed) saveName(user);
                        }}
                      />
                    </td>
                    <td>{user.email}</td>
                    <td style={{ minWidth: 220 }}>
                      {user.groups.length ? (
                        <div className="d-flex flex-wrap gap-1">
                          {user.groups.map((group) => (
                            <Badge key={group._id} bg="secondary">
                              {group.name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted small">No groups</span>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!changed || savingId !== null}
                        onClick={() => saveName(user)}
                      >
                        {savingId === user._id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Save size={16} />
                        )}
                        <span className="ms-2">Save</span>
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {data?.results.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No users match this search.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {data && (
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <small className="text-muted">
                {data.pagination.total === 0
                  ? "0 users"
                  : `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(
                      page * PAGE_SIZE,
                      data.pagination.total
                    )} of ${data.pagination.total} users`}
              </small>
              {data.pagination.totalPages > 1 && (
                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Previous
                  </Button>
                  <small className="text-muted">
                    Page {page} of {data.pagination.totalPages}
                  </small>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={page >= data.pagination.totalPages || loading}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
};
